import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle2, Phone, Calculator, X, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
const steps = [{
  number: 1,
  title: 'Assess & Measure Your Driveway',
  image: 'https://cdn.magicpatterns.com/uploads/2ebE9ciuiVZZKZHNZLJiZ5/measuring_gravel_driveway.png',
  description: 'Start by measuring the length, width, and noting the current condition of your dirt driveway. Mark the boundaries with stakes and string. Calculate the square footage — this determines how much material you need.',
  tips: ['Measure length × width in feet', 'Plan for 4–6 inches of base material depth', 'Use our Calculator to estimate yards needed', 'Account for any slope or drainage needs']
}, {
  number: 2,
  title: 'Clear & Grade the Surface',
  image: 'https://cdn.magicpatterns.com/uploads/gQYsAvdSYDiWUkNHGFHmQH/grading_driveway.png',
  description: 'Remove any vegetation, large rocks, and debris from the driveway area. Use a rake or skid steer to grade the surface so water drains to the sides. A slight crown (higher in the center) prevents puddles.',
  tips: ['Remove all grass, weeds, and roots', 'Create a 2% slope from center to edges', 'Fill any low spots with fill dirt', 'Compact the bare soil with a plate compactor']
}, {
  number: 3,
  title: 'Install Landscape Fabric (Optional)',
  image: 'https://cdn.magicpatterns.com/uploads/9QXpjprgL7K5DjWViDEHz9/spreading_the_fabric_driveway.png',
  description: 'Lay commercial-grade landscape fabric over the graded dirt. This prevents weeds from growing up through your gravel and helps separate the soil from the rock layers. Overlap seams by 6–12 inches.',
  tips: ['Use commercial-grade fabric, not cheap weed barrier', 'Overlap seams by at least 6 inches', 'Secure with landscape staples every 3 feet', 'This step saves major headaches down the road']
}, {
  number: 4,
  title: 'Spread the Base Layer — 1.25" Minus',
  image: 'https://cdn.magicpatterns.com/uploads/tpiwXZ7h5VKuZJngK4ptsh/driveway_gravel_spreading_in_tacoma.png',
  description: 'The base layer is the foundation of your driveway. Spread 3–4 inches of 1.25" Minus crushed rock evenly across the surface. This coarser material locks together and creates a rock-solid base that won\'t shift.',
  tips: ['Order 1.25" Minus from our catalog', 'Spread evenly with a rake to 3–4" depth', 'Compact thoroughly with a plate compactor', 'Make multiple passes until the surface is firm'],
  material: '1.25" Minus',
  materialSlug: '1-25-minus'
}, {
  number: 5,
  title: 'Compact the Base Layer',
  image: 'https://cdn.magicpatterns.com/uploads/hkbT29dFvc9BT49e5MH1GV/compacting_base_layer.png',
  description: 'Rent a plate compactor (available at most equipment rental shops) and make several passes over the base layer. Wet the material lightly before compacting — moisture helps the fines bind together for maximum density.',
  tips: ['Lightly wet the gravel before compacting', 'Make 3–4 passes in different directions', 'The surface should feel solid underfoot', 'Add more material to any low spots and re-compact']
}, {
  number: 6,
  title: 'Add the Top Layer — 5/8" Minus',
  image: 'https://cdn.magicpatterns.com/uploads/pqds5pqkfBofDzHJ4pjSBj/base_layer_compacting.png',
  description: 'Now spread 2–3 inches of 5/8" Minus crushed rock on top. This is the finished driving surface — it packs down tight, sheds water, and provides a smooth, firm surface for vehicles and foot traffic.',
  tips: ['Order 5/8" Minus — our most popular driveway gravel', 'Spread to 2–3" depth over the compacted base', 'Rake smooth and maintain the crown shape', 'Compact one final time for a professional finish'],
  material: '5/8" Minus',
  materialSlug: '5-8-minus'
}, {
  number: 7,
  title: 'Final Compaction & Edging',
  image: 'https://cdn.magicpatterns.com/uploads/7Nv3L8waV4wtGihPojgZkN/fina_product_gravel_driveway_in_tacoma.png',
  description: 'Give the top layer a final compaction pass. Optionally, install edging (timber, steel, or stone) along the sides to keep gravel contained. Your new driveway is ready to use — it will continue to settle and harden over the first few weeks.',
  tips: ['Edging prevents gravel from migrating into the yard', 'Drive on it gently for the first week', 'Top off with a thin layer of 5/8" Minus annually', 'Keep drainage channels clear of debris']
}];
const projectTypes = [{
  id: 'driveway',
  name: 'Driveway',
  defaultDepth: 4
}, {
  id: 'patio',
  name: 'Patio Base',
  defaultDepth: 4
}, {
  id: 'walkway',
  name: 'Walkway',
  defaultDepth: 3
}, {
  id: 'parking',
  name: 'Parking Pad',
  defaultDepth: 6
}, {
  id: 'french-drain',
  name: 'French Drain',
  defaultDepth: 12
}, {
  id: 'fill',
  name: 'General Fill',
  defaultDepth: 6
}];
const calcMaterials = [{
  id: 'crushed-rock',
  name: '5/8" Minus Crushed Rock',
  density: 1.4,
  basePrice: 55
}, {
  id: '125-minus',
  name: '1.25" Minus',
  density: 1.4,
  basePrice: 51
}, {
  id: '58-clear',
  name: '5/8" Clean',
  density: 1.35,
  basePrice: 58
}, {
  id: '125-clear',
  name: '1.25" Clean',
  density: 1.35,
  basePrice: 53
}, {
  id: 'quarry-rock',
  name: '2-4" Quarry Spalls',
  density: 1.5,
  basePrice: 61
}, {
  id: 'pea-gravel',
  name: 'Pea Gravel',
  density: 1.45,
  basePrice: 58
}, {
  id: 'river-rock',
  name: 'River Rock',
  density: 1.5,
  basePrice: 69
}, {
  id: 'topsoil',
  name: '3-Way Topsoil',
  density: 1.1,
  basePrice: 48
}];
function getPriceRange(basePrice: number) {
  const minPrice = basePrice;
  const maxSurcharge = 1 + (10 - 5) * 0.1; // 1.5x at 5 yards (smallest order)
  const maxPrice = Math.ceil(basePrice * maxSurcharge);
  return {
    minPrice,
    maxPrice
  };
}
export function DiyHelp() {
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [projectType, setProjectType] = useState('driveway');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [depth, setDepth] = useState('4');
  const [material, setMaterial] = useState('crushed-rock');
  const [calcResults, setCalcResults] = useState<{
    yards: number;
    tons: number;
  } | null>(null);
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const l = parseFloat(length);
    const w = parseFloat(width);
    const d = parseFloat(depth);
    if (isNaN(l) || isNaN(w) || isNaN(d)) return;
    const depthInFeet = d / 12;
    const cubicFeet = l * w * depthInFeet;
    const cubicYards = cubicFeet / 27;
    const selectedMaterial = calcMaterials.find(m => m.id === material);
    const density = selectedMaterial ? selectedMaterial.density : 1.4;
    const tons = cubicYards * density;
    setCalcResults({
      yards: Math.ceil(cubicYards * 10) / 10,
      tons: Math.ceil(tons * 10) / 10
    });
  };
  return <main className="w-full bg-[#fdfbf7] dark:bg-[#2a2a2a] transition-colors duration-300" data-id="element-1275">
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-[#333333] overflow-hidden" data-id="element-1276">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
        backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/xhbAQurLqAJWTT1L23JwMV/driveway-gravel.jpg)'
      }} data-id="element-1277" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#333333]/40 to-[#333333]/50" data-id="element-1278" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1279">
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-brand-orange font-bold text-sm uppercase tracking-widest mb-4" data-id="element-1280">
            DIY Guide
          </motion.p>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-6" data-id="element-1281">
            How to Build a Perfect{' '}
            <span className="text-brand-orange" data-id="element-1282">Gravel Driveway</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-lg text-stone-300 font-medium font-sub max-w-2xl mx-auto" data-id="element-1283">
            A complete step-by-step guide to transforming a dirt driveway into a
            professional, long-lasting gravel surface. No contractor needed.
          </motion.p>
        </div>
      </section>

      {/* Quick Info Bar */}
      <section className="bg-[#3a3a3a] border-y border-[#4a4a4a] py-5" data-id="element-1284">
        <div className="max-w-4xl mx-auto px-4 flex flex-wrap justify-center gap-x-10 gap-y-3 text-sm text-stone-300 font-medium" data-id="element-1285">
          <span data-id="element-1286">⏱ Weekend project</span>
          <span data-id="element-1287">📏 Any driveway size</span>
          <span data-id="element-1288">💰 ~$3–5/sq ft</span>
          <span data-id="element-1289">🔧 Basic tools needed</span>
        </div>
      </section>

      {/* Materials You'll Need */}
      <section className="py-16 border-b border-stone-200 dark:border-[#444444]" data-id="element-1290">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1291">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-1292">
            <h2 className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white mb-6" data-id="element-1293">
              Materials You'll Need
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1294">
              {[{
              name: '1.25" Minus Crushed Rock',
              use: 'Base layer — compacts into a solid foundation',
              slug: '1-25-minus'
            }, {
              name: '5/8" Minus Crushed Rock',
              use: 'Top layer — smooth, firm driving surface',
              slug: '5-8-minus'
            }, {
              name: 'Landscape Fabric',
              use: 'Weed barrier between soil and gravel',
              slug: null
            }, {
              name: 'Fill Dirt (if needed)',
              use: 'Level out low spots before gravel',
              slug: null
            }].map(item => <div key={item.name} className="flex items-start gap-3 p-4 bg-white dark:bg-[#373737] rounded-xl border border-stone-200 dark:border-[#4a4a4a]" data-id="element-1295">
                  <CheckCircle2 className="w-5 h-5 text-brand-orange mt-0.5 shrink-0" data-id="element-1296" />
                  <div data-id="element-1297">
                    {item.slug ? <Link to={`/shop/${item.slug}`} className="font-bold text-brand-orange hover:underline text-sm" data-id="element-1298">
                        {item.name}
                      </Link> : <p className="font-bold text-brand-charcoal dark:text-white text-sm" data-id="element-1299">
                        {item.name}
                      </p>}
                    <p className="text-xs text-brand-muted dark:text-stone-400 mt-0.5" data-id="element-1300">
                      {item.use}
                    </p>
                  </div>
                </div>)}
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3" data-id="element-1301">
              <button onClick={() => {
              setCalcResults(null);
              setShowCalcModal(true);
            }} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-brand-button hover:bg-brand-buttonHover text-white font-bold text-sm rounded-none transition-colors" data-id="element-1302">
                <Calculator className="w-4 h-4" data-id="element-1303" />
                Calculate How Much You Need
              </button>
              <Link to="/#materials" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-stone-300 dark:border-[#555555] text-brand-charcoal dark:text-white font-bold text-sm rounded-none hover:bg-stone-100 dark:hover:bg-[#444444] transition-colors" data-id="element-1304">
                Browse Our Catalog
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16" data-id="element-1305">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1306">
          <div className="space-y-20" data-id="element-1307">
            {steps.map((step, idx) => <motion.article key={step.number} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            delay: 0.1
          }} className="relative" data-id="element-1308">
                {/* Step number accent */}
                <div className="flex items-center gap-4 mb-6" data-id="element-1309">
                  <span className="flex items-center justify-center w-10 h-10 bg-brand-orange text-white font-heading font-extrabold text-lg rounded-none shrink-0" data-id="element-1310">
                    {step.number}
                  </span>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-brand-charcoal dark:text-white" data-id="element-1311">
                    {step.title}
                  </h3>
                </div>

                {/* Image */}
                <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden mb-6" data-id="element-1312">
                  <img src={step.image} alt={step.title} className="w-full h-full object-cover" data-id="element-1313" />
                </div>

                {/* Description */}
                <p className="text-brand-charcoal/85 dark:text-stone-300 text-base leading-relaxed mb-6" data-id="element-1314">
                  {step.description}
                </p>

                {/* Tips */}
                <div className="bg-stone-50 dark:bg-[#333333] border border-stone-200 dark:border-[#4a4a4a] rounded-xl p-5" data-id="element-1315">
                  <p className="text-xs font-bold text-brand-muted dark:text-stone-400 uppercase tracking-wider mb-3" data-id="element-1316">
                    Pro Tips
                  </p>
                  <ul className="space-y-2" data-id="element-1317">
                    {step.tips.map((tip, i) => <li key={i} className="flex items-start gap-2 text-sm text-brand-charcoal/80 dark:text-stone-300" data-id="element-1318">
                        <CheckCircle2 className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-1319" />
                        {tip}
                      </li>)}
                  </ul>
                </div>

                {/* Material callout */}
                {step.material && <div className="mt-4 flex items-center gap-3 p-4 bg-brand-orange/10 border border-brand-orange/20 rounded-xl" data-id="element-1320">
                    <span className="text-sm font-bold text-brand-orange" data-id="element-1321">
                      Recommended:
                    </span>
                    <Link to={`/shop/${(step as any).materialSlug}`} className="text-sm text-brand-charcoal dark:text-white font-medium hover:text-brand-orange transition-colors" data-id="element-1322">
                      {step.material}
                    </Link>
                    <Link to={`/shop/${(step as any).materialSlug}`} className="ml-auto text-xs font-bold text-brand-orange hover:underline" data-id="element-1323">
                      View Product →
                    </Link>
                  </div>}
              </motion.article>)}
          </div>

          {/* SEO Block: After Step 3 area - Local context */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mt-20 p-6 md:p-8 bg-white dark:bg-[#333333] border border-stone-200 dark:border-[#4a4a4a] rounded-xl" data-id="element-1324">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-1325">
              Gravel Delivery in Tacoma &amp; South King County
            </h3>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed mb-3" data-id="element-1326">
              Whether you're building a new gravel driveway in Puyallup,
              replacing a muddy parking pad in Bonney Lake, or resurfacing a
              rural road in Enumclaw, having the right materials delivered on
              time makes all the difference. 253Gravel provides same-day and
              next-day bulk gravel delivery across Tacoma, Lakewood, Federal
              Way, Auburn, Kent, Sumner, Edgewood, and the surrounding South
              Sound communities.
            </p>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed" data-id="element-1327">
              Our dump trucks carry 5–12 yards per load and can place material
              directly on your driveway, jobsite, or backyard — no wheelbarrow
              relay required. We know the local soil conditions (hello, Puget
              Sound clay) and can recommend the right base and top layers for
              driveways that last through Pacific Northwest winters.
            </p>
          </motion.div>

          {/* SEO Block: Popular local projects */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mt-12 p-6 md:p-8 bg-stone-50 dark:bg-[#373737] border border-stone-200 dark:border-[#4a4a4a] rounded-xl" data-id="element-1328">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-1329">
              Common DIY Gravel Projects in the South Sound
            </h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-brand-charcoal/80 dark:text-stone-300" data-id="element-1330">
              {['Driveway resurfacing in Tacoma & Lakewood', 'New gravel driveways in Bonney Lake & Enumclaw', 'French drain installations in Auburn & Kent', 'Parking pad construction in Puyallup & Sumner', 'Landscaping ground cover in Federal Way & Milton', 'Road base for rural properties in South King County'].map((item, i) => <li key={i} className="flex items-start gap-2" data-id="element-1331">
                  <CheckCircle2 className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-1332" />
                  {item}
                </li>)}
            </ul>
          </motion.div>

          {/* SEO Block: Why local matters */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mt-12 p-6 md:p-8 bg-white dark:bg-[#333333] border border-stone-200 dark:border-[#4a4a4a] rounded-xl" data-id="element-1333">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-1334">
              Why Buy Gravel Locally in Tacoma?
            </h3>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed mb-3" data-id="element-1335">
              Big-box stores sell gravel by the bag — but for driveways and
              serious landscaping, you need bulk delivery by the yard. Buying
              locally from 253Gravel means lower delivery costs, fresher
              material sourced from regional quarries, and drivers who know how
              to navigate Tacoma's hills, South King County's rural roads, and
              everything in between.
            </p>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed" data-id="element-1336">
              We're based in the 253 area code and deliver to Pierce County and
              South King County daily. No middlemen, no markups from national
              chains — just honest pricing on crushed rock, drain rock, topsoil,
              bark, and recycled aggregates delivered straight to your property.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#333333] border-t border-[#4a4a4a]" data-id="element-1337">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1338">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-white mb-4" data-id="element-1339">
            Ready to Get Started?
          </h2>
          <p className="text-stone-300 font-medium font-sub mb-8 max-w-lg mx-auto" data-id="element-1340">
            We deliver gravel, crushed rock, and all the materials you need
            straight to your driveway. Same-day delivery available.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1341">
            <Link to="/#materials" className="px-8 py-3.5 bg-brand-button hover:bg-brand-buttonHover text-white font-bold rounded-none transition-colors flex items-center justify-center" data-id="element-1342">
              Order Materials
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-1343" />
            </Link>
            <a href="tel:206-619-3742" className="px-8 py-3.5 border border-white/30 bg-white/10 text-white font-bold rounded-none hover:bg-white hover:text-stone-900 transition-all flex items-center justify-center" data-id="element-1344">
              <Phone className="w-4 h-4 mr-2" data-id="element-1345" />
              (206) 619-3742
            </a>
          </div>
        </div>
      </section>

      {/* Calculator Modal */}
      <AnimatePresence data-id="element-1346">
        {showCalcModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-1347">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowCalcModal(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-1348" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="relative w-full max-w-4xl bg-[#474747] border border-[#555555] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]" data-id="element-1349">
              <div className="flex items-center justify-between p-6 border-b border-[#555555] shrink-0" data-id="element-1350">
                <h3 className="text-xl font-bold text-white flex items-center" data-id="element-1351">
                  <Calculator className="w-5 h-5 mr-2 text-brand-orange" data-id="element-1352" />
                  Material Calculator
                </h3>
                <button onClick={() => setShowCalcModal(false)} className="text-stone-400 hover:text-white transition-colors p-1" data-id="element-1353">
                  <X className="w-6 h-6" data-id="element-1354" />
                </button>
              </div>
              <div className="overflow-y-auto" data-id="element-1355">
                <div className="grid grid-cols-1 md:grid-cols-5" data-id="element-1356">
                  <div className="p-6 sm:p-8 md:col-span-3 border-b md:border-b-0 md:border-r border-[#555555]" data-id="element-1357">
                    <form onSubmit={handleCalculate} className="space-y-6" data-id="element-1358">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1359">
                        <div data-id="element-1360">
                          <label className="block text-sm font-medium text-stone-300 mb-2" data-id="element-1361">
                            Project Type
                          </label>
                          <select value={projectType} onChange={e => {
                        setProjectType(e.target.value);
                        const p = projectTypes.find(pt => pt.id === e.target.value);
                        if (p) setDepth(p.defaultDepth.toString());
                      }} className="w-full px-4 py-3 bg-[#3a3a3a] border border-[#555555] rounded-xl text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" data-id="element-1362">
                            {projectTypes.map(p => <option key={p.id} value={p.id} data-id="element-1363">
                                {p.name}
                              </option>)}
                          </select>
                        </div>
                        <div data-id="element-1364">
                          <label className="block text-sm font-medium text-stone-300 mb-2" data-id="element-1365">
                            Material
                          </label>
                          <select value={material} onChange={e => setMaterial(e.target.value)} className="w-full px-4 py-3 bg-[#3a3a3a] border border-[#555555] rounded-xl text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" data-id="element-1366">
                            {calcMaterials.map(m => <option key={m.id} value={m.id} data-id="element-1367">
                                {m.name}
                              </option>)}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4" data-id="element-1368">
                        <div data-id="element-1369">
                          <label className="block text-sm font-medium text-stone-300 mb-2" data-id="element-1370">
                            Length (ft)
                          </label>
                          <input type="number" value={length} onChange={e => setLength(e.target.value)} placeholder="0" className="w-full px-4 py-3 bg-[#3a3a3a] border border-[#555555] rounded-xl text-white placeholder-stone-500 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-1371" />
                        </div>
                        <div data-id="element-1372">
                          <label className="block text-sm font-medium text-stone-300 mb-2" data-id="element-1373">
                            Width (ft)
                          </label>
                          <input type="number" value={width} onChange={e => setWidth(e.target.value)} placeholder="0" className="w-full px-4 py-3 bg-[#3a3a3a] border border-[#555555] rounded-xl text-white placeholder-stone-500 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-1374" />
                        </div>
                        <div data-id="element-1375">
                          <label className="block text-sm font-medium text-stone-300 mb-2" data-id="element-1376">
                            Depth (in)
                          </label>
                          <input type="number" value={depth} onChange={e => setDepth(e.target.value)} className="w-full px-4 py-3 bg-[#3a3a3a] border border-[#555555] rounded-xl text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-1377" />
                        </div>
                      </div>
                      <button type="submit" className="w-full bg-brand-button hover:bg-brand-buttonHover text-white py-4 rounded-xl font-medium transition-colors flex items-center justify-center" data-id="element-1378">
                        <Calculator className="w-5 h-5 mr-2" data-id="element-1379" />
                        Calculate Need
                      </button>
                    </form>
                  </div>
                  <div className="p-6 sm:p-8 md:col-span-2 bg-[#3a3a3a] flex flex-col justify-center" data-id="element-1380">
                    {calcResults ? <div className="space-y-6" data-id="element-1381">
                        <div data-id="element-1382">
                          <p className="text-sm text-stone-400 font-medium mb-1" data-id="element-1383">
                            Estimated Volume
                          </p>
                          <div className="flex items-baseline gap-2" data-id="element-1384">
                            <span className="font-heading text-4xl font-bold text-white" data-id="element-1385">
                              {calcResults.yards}
                            </span>
                            <span className="text-stone-400 font-medium" data-id="element-1386">
                              Cubic Yards
                            </span>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-[#555555]" data-id="element-1387">
                          <p className="text-sm text-stone-400 font-medium mb-1" data-id="element-1388">
                            Estimated Weight
                          </p>
                          <div className="flex items-baseline gap-2" data-id="element-1389">
                            <span className="font-heading text-3xl font-bold text-white" data-id="element-1390">
                              {calcResults.tons}
                            </span>
                            <span className="text-stone-400 font-medium" data-id="element-1391">
                              Tons
                            </span>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-[#555555]" data-id="element-1392">
                          <p className="text-sm text-stone-400 font-medium mb-1" data-id="element-1393">
                            Material Price Range
                          </p>
                          {(() => {
                      const mat = calcMaterials.find(m => m.id === material);
                      if (!mat) return null;
                      const {
                        minPrice,
                        maxPrice
                      } = getPriceRange(mat.basePrice);
                      return <>
                                <p className="text-lg font-medium text-white" data-id="element-1394">
                                  ${minPrice} – ${maxPrice}{' '}
                                  <span className="text-sm text-stone-500 font-normal" data-id="element-1395">
                                    / yard
                                  </span>
                                </p>
                                <p className="text-xs text-stone-400 mt-1" data-id="element-1396">
                                  ${minPrice}/yd at 10+ yards · ${maxPrice}/yd
                                  at 5 yards
                                </p>
                              </>;
                    })()}
                          <p className="text-xs text-stone-500 mt-1 flex items-start" data-id="element-1397">
                            <Info className="w-3 h-3 mr-1 mt-0.5 shrink-0" data-id="element-1398" />
                            Delivery fee not included. Final price depends on
                            exact location.
                          </p>
                        </div>
                      </div> : <div className="text-center text-stone-500" data-id="element-1399">
                        <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" data-id="element-1400" />
                        <p data-id="element-1401">
                          Enter your project dimensions to see estimated
                          materials needed.
                        </p>
                      </div>}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </main>;
}